import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { Field, Loading, Tag } from '../ui.tsx';

/**
 * Páginas abertas por link, sem depender de e-mail:
 * redefinição de senha e aceite de convite.
 */

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador', editor: 'Editor', analyst: 'Analista', viewer: 'Visualizador',
};

function Frame({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="brand" style={{ padding: 0, marginBottom: 20 }}>
          <span className="brand-mark" />
          <div>
            <div className="brand-name">Orbs</div>
            <div className="brand-sub">Vídeo interativo</div>
          </div>
        </div>
        <h1 className="page-title" style={{ fontSize: 19 }}>{title}</h1>
        {subtitle && <p className="page-sub" style={{ marginBottom: 20 }}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

export function ResetPasswordPage(): JSX.Element {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [state, setState] = useState<{ loading: boolean; valid: boolean; reason?: string; email?: string; name?: string }>({
    loading: true, valid: false,
  });
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, valid: false, reason: 'not_found' });
      return;
    }
    api<{ valid: boolean; reason?: string; email?: string; name?: string }>(
      `/api/auth/reset/${encodeURIComponent(token)}`,
      { workspace: null },
    )
      .then((data) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, valid: false, reason: 'not_found' }));
  }, [token]);

  if (state.loading) {
    return <Frame title="Verificando o link"><Loading /></Frame>;
  }

  if (!state.valid) {
    const messages: Record<string, string> = {
      not_found: 'Este link não é válido.',
      expired: 'Este link expirou. Links de redefinição valem 1 hora.',
      used: 'Este link já foi usado. Peça outro.',
    };
    return (
      <Frame title="Link indisponível">
        <div className="alert error" style={{ marginBottom: 16 }}>
          {messages[state.reason ?? 'not_found'] ?? 'Este link não é válido.'}
        </div>
        <p className="card-sub">
          Peça um link novo a quem administra o workspace. Se você é a única pessoa com acesso, use no servidor:
        </p>
        <div className="code-block">npm run reset-password -- --email seu@email.com</div>
        <a className="btn ghost block" style={{ marginTop: 16 }} href="/">Voltar para a entrada</a>
      </Frame>
    );
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (password !== confirmation) {
      setError('As duas senhas precisam ser iguais.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/reset', { body: { token, password }, workspace: null });
      // A redefinição já abre a sessão: segue direto para o painel.
      window.location.href = '/app/videos';
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível redefinir a senha.');
      setBusy(false);
    }
  }

  return (
    <Frame title="Definir uma nova senha" subtitle={`Conta ${state.email}`}>
      <form onSubmit={submit}>
        <Field label="Nova senha" id="np" hint="Mínimo de 8 caracteres, com letras e números.">
          <input id="np" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" autoFocus />
        </Field>
        <Field label="Repita a nova senha" id="np2">
          <input id="np2" type="password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required minLength={8} autoComplete="new-password" />
        </Field>
        {error && <div className="alert error" style={{ marginBottom: 14 }}>{error}</div>}
        <button type="submit" className="btn block" disabled={busy}>
          {busy ? <span className="spinner-inline" /> : null} Salvar e entrar
        </button>
      </form>
      <p className="card-sub" style={{ marginTop: 14 }}>
        Ao salvar, todas as outras sessões dessa conta são encerradas.
      </p>
    </Frame>
  );
}

export function InvitePage(): JSX.Element {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [state, setState] = useState<{
    loading: boolean; valid: boolean; reason?: string; workspace?: string; role?: string;
    note?: string; authenticated?: boolean;
  }>({ loading: true, valid: false });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, valid: false, reason: 'not_found' });
      return;
    }
    api<any>(`/api/invites/${encodeURIComponent(token)}`, { workspace: null })
      .then((data) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, valid: false, reason: 'not_found' }));
  }, [token]);

  if (state.loading) return <Frame title="Verificando o convite"><Loading /></Frame>;

  if (!state.valid) {
    const messages: Record<string, string> = {
      not_found: 'Este convite não é válido.',
      expired: 'Este convite expirou. Peça um link novo.',
      used: 'Este convite já foi utilizado.',
      revoked: 'Este convite foi cancelado.',
    };
    return (
      <Frame title="Convite indisponível">
        <div className="alert error">{messages[state.reason ?? 'not_found']}</div>
        <a className="btn ghost block" style={{ marginTop: 16 }} href="/">Voltar para a entrada</a>
      </Frame>
    );
  }

  async function accept(event?: React.FormEvent): Promise<void> {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/invites/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        workspace: null,
        ...(state.authenticated ? {} : { body: { name, email, password } }),
      });
      window.location.href = '/app/videos';
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível aceitar o convite.');
      setBusy(false);
    }
  }

  return (
    <Frame
      title={`Convite para ${state.workspace}`}
      subtitle={state.note || 'Você foi convidado a colaborar neste workspace.'}
    >
      <p style={{ marginTop: 0 }}>
        Papel: <Tag tone="info">{ROLE_LABEL[state.role ?? ''] ?? state.role}</Tag>
      </p>

      {state.authenticated ? (
        <>
          <p className="card-sub">Você já está autenticado. É só aceitar.</p>
          {error && <div className="alert error" style={{ marginBottom: 14 }}>{error}</div>}
          <button type="button" className="btn block" disabled={busy} onClick={() => accept()}>
            {busy ? <span className="spinner-inline" /> : null} Entrar no workspace
          </button>
        </>
      ) : (
        <form onSubmit={accept}>
          <p className="card-sub">Crie sua conta para aceitar. Se já tem conta, entre primeiro e abra o link de novo.</p>
          <Field label="Seu nome" id="in"><input id="in" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} autoComplete="name" /></Field>
          <Field label="E-mail" id="ie"><input id="ie" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" /></Field>
          <Field label="Senha" id="ip" hint="Mínimo de 8 caracteres, com letras e números.">
            <input id="ip" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
          </Field>
          {error && <div className="alert error" style={{ marginBottom: 14 }}>{error}</div>}
          <button type="submit" className="btn block" disabled={busy}>
            {busy ? <span className="spinner-inline" /> : null} Criar conta e entrar
          </button>
        </form>
      )}
      <a className="btn ghost block" style={{ marginTop: 12 }} href="/">Cancelar</a>
    </Frame>
  );
}
